import React from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const UPDATED_AT = '23 June 2026';
const CONTACT_EMAIL = 'support@shiftwagon.co.za';

const retainedRecords = [
  'Completed, cancelled, refunded, disputed, or archived booking records needed for accounting, dispute, safety, fraud prevention, or legal reasons.',
  'Payment, refund, payout, ledger, Paystack subaccount, settlement, tax, and reconciliation records that must be retained for financial controls.',
  'Driver verification, safety, consent, compliance, document review, and admin audit records where retention is necessary to protect users and the platform.',
  'Support, messaging, notification, and security records needed to resolve open issues, investigate abuse, prevent fraud, or comply with legal obligations.',
  'Backups and logs that are retained temporarily according to operational backup and security practices before normal expiry or rotation.',
];

const deletionSteps = [
  {
    title: 'Use the app when possible',
    body:
      'Customers and drivers can request account deletion from the profile or settings area of the relevant mobile app. This is the fastest route because it starts from a signed-in account.',
  },
  {
    title: 'Email a deletion request',
    body: `If you cannot access the app, email ${CONTACT_EMAIL} from the email address linked to your ShiftWagon account. Include whether the request is for a customer account, driver account, or company owner account.`,
  },
  {
    title: 'Verify identity',
    body:
      'We may ask for reasonable information to confirm that the requester controls the account. For driver or company accounts, we may need additional verification before removing or limiting access to compliance and payout records.',
  },
  {
    title: 'Review active work and retained records',
    body:
      'Deletion may be delayed or limited while there are active trips, unresolved support issues, pending refunds, payout reviews, chargebacks, legal holds, or safety investigations.',
  },
  {
    title: 'Confirm the result',
    body:
      'Once processed, account access and ordinary profile data are removed or deactivated where possible. Records that must be retained are restricted to their required operational, financial, legal, safety, or audit purpose.',
  },
];

const DataDeletionPage: React.FC = () => {
  return (
    <div className="landing-shell">
      <Header />
      <main>
        <section className="landing-hero landing-hero-simple policy-hero">
          <div className="container policy-hero-content">
            <span className="landing-badge">Data Deletion</span>
            <h1 className="landing-heading">Request deletion of a ShiftWagon account or personal data.</h1>
            <p className="landing-subheading">
              Last updated: {UPDATED_AT}. This page explains how customers, drivers, and company
              owners can request deletion and what records may need to be retained.
            </p>
          </div>
        </section>

        <section className="landing-section policy-section">
          <div className="container policy-layout">
            <aside className="policy-summary">
              <h2>Deletion Request</h2>
              <p>
                The preferred method is the in-app delete account button. If you cannot sign in,
                email us from the account email address and we will guide verification.
              </p>
              <a className="landing-primary-action" href={`mailto:${CONTACT_EMAIL}?subject=ShiftWagon%20Data%20Deletion%20Request`}>
                Email Request
              </a>
            </aside>

            <div className="policy-content">
              <article className="policy-card">
                <h2>What can be deleted</h2>
                <p>
                  Subject to identity verification and legal retention requirements, we can delete,
                  deactivate, anonymize, or restrict ordinary account profile records, communication
                  preferences, saved places, app push tokens, profile photos, and other personal data
                  that is no longer needed to provide services or meet legal obligations.
                </p>
              </article>

              <article className="policy-card">
                <h2>How to request deletion</h2>
                <ol>
                  {deletionSteps.map((step) => (
                    <li key={step.title}>
                      <strong>{step.title}</strong>
                      <span>{step.body}</span>
                    </li>
                  ))}
                </ol>
              </article>

              <article className="policy-card">
                <h2>Records we may retain</h2>
                <p>
                  Some records cannot be fully deleted immediately because ShiftWagon must protect
                  customers, drivers, payment integrity, tax records, safety investigations, and
                  legal obligations. Retained records are kept for the purpose that requires
                  retention and are not treated as an active public profile.
                </p>
                <ul>
                  {retainedRecords.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </article>

              <article className="policy-card">
                <h2>Expected timing</h2>
                <p>
                  We aim to acknowledge deletion requests within a reasonable period after identity
                  verification. Completion time depends on active bookings, payment state, support
                  matters, document review status, and legal retention requirements. We will explain
                  if a request is delayed, partially completed, or restricted by required retention.
                </p>
              </article>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default DataDeletionPage;
